import { BCMSDate } from "@bcm-dev/bcms-vite";

/**
 * @class WireSearch
 * @classdesc Class definition for controlling the properties that are set in a search feature.
 * 
 * @property {STRING} _status - The status to filter by
 * @property {STRING} _fundingAccountID - The fundingAccountID to filter by
 * @property {STRING} _dateRange - The date range to filter by
 * @property {BCMSDate} _fromDate - The fromDate to filter by
 * @property {BCMSDate} _toDate - The toDate to filter by
 * @property {STRING} _referenceNumber - The referenceNumber to filter by
 */
export default class WireSearch {
    _status;
    _fundingAccountID;
    _dateRange;
    _fromDate;
    _toDate;
    _referenceNumber;

    constructor(record) {
        this.status = record.status;
        this.fundingAccountID = record.fundingAccountID;
        this.dateRange = record.dateRange;
        this.fromDate = record.fromDate;
        this.toDate = record.toDate;
    }

    /**
     * @name status
     * @instance
     * @property set {STRING} - sets the status
     * @memberof WireSearch
     */
    set status(value) { this._status = value; }

    /**
     * @name fundingAccountID
     * @instance
     * @property set {STRING} - sets the fundingAccountID
     * @memberof WireSearch
     */
    set fundingAccountID(value) { this._fundingAccountID = value; }

    /**
     * @name dateRange
     * @instance
     * @property set {STRING} - sets the dateRange
     * @memberof WireSearch
     */
    set dateRange(value) { this._dateRange = value; }

    /**
     * @name fromDate
     * @instance
     * @property set {BCMSDate} - sets the fromDate
     * @memberof WireSearch
     */
    set fromDate(value) { this._fromDate = value; }

    /**
     * @name toDate
     * @instance
     * @property set {BCMSDate} - sets the toDate
     * @memberof WireSearch
     */
    set toDate(value) { this._toDate = value; }

    /**
     * @name referenceNumber
     * @instance
     * @property set {BCMSDate} - sets the referenceNumber
     * @memberof WireSearch
     */
    set referenceNumber(value) { this._referenceNumber = value; }

    /**
     * @name fundingAccountID
     * @instance
     * @property get {Map} - returns the fundingAccountID
     * @memberof WireSearch
     */
    get fundingAccountID() { return this._fundingAccountID; }

    /**
     * @name status
     * @instance
     * @property get {Map} - returns the status
     * @memberof WireSearch
     */
    get status() { return this._status; }

    /**
     * @name dateRange
     * @instance
     * @property get {Map} - returns the dateRange
     * @memberof WireSearch
     */
    get dateRange() { return this._dateRange; }

    /**
     * @name fromDate
     * @instance
     * @property get {Map} - returns the fromDate
     * @memberof WireSearch
     */
    get fromDate() { return this._fromDate; }

    /**
     * @name toDate
     * @instance
     * @property get {Map} - returns the toDate
     * @memberof WireSearch
     */
    get toDate() { return this._toDate; }

    /**
     * @name referenceNumber
     * @instance
     * @property get {Map} - returns the referenceNumber
     * @memberof WireSearch
     */
    get referenceNumber() { return this._referenceNumber; }

    /**
     * @name filters
     * @instance
     * @property get {OBJECT} - returns the set filter properties
     * @memberof WireSearch
     */
    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    /*get filters() {
        return Object.getOwnPropertyNames(this).reduce((result, name) => {
            const key = name.slice(1);
            const value = this[key];

            if(value !== undefined && value !== "" && (key !== "ranges" && key !== "types")){
                result[key] = value;
            }

            return result;
        }, {});
    }*/
    
    /**
     * @name record
     * @instance
     * @property get {OBJECT} - returns the set values
     * @memberof WireSearch
     */
    get record() {
        return {
            status: this.status,
            fundingAccountID: this.fundingAccountID,
            dateRange: this.dateRange,
            fromDate: this.fromDate,
            toDate: this.toDate,
            referenceNumber: this.referenceNumber
        };
    }

    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    /*checkValue(transaction, key) {
        switch(key) {
            case "date":
                return transaction.date.isAfter(BCMSDate.init().offset(this.date));
    
            case "description":
                return transaction.description.toLowerCase().indexOf(this.description.toLowerCase()) > -1;
    
            case "type":
                return this.type === "all" ? true : transaction.filterType === this.type;
    
            case "amount":
                const amount = parseFloat(transaction.amount.amount);
                return parseFloat(this.amount.amount) === (amount < 0 ? -amount : amount);
    
            case "check":
                const desc = transaction.description;
                return desc.toLowerCase().indexOf("check") > -1 && desc.indexOf(this.check) > -1;
    
            default:
                return true;
        }
    }*/

    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    /*filter() {
        return Transaction.initByAccount(this.accountID)
            .filter(transaction => {
                return Object.keys(this.filters).reduce((result, key) => {
                    if(!this.checkValue(transaction, key)) {
                        result = false;
                    }

                    return result;
                }, true);
            }, []);
    }*/

    reset() {
        const vals = { 
            status: this.status,
            fundingAccountID: this.fundingAccountID,
            dateRange: this.dateRange,
            fromDate: this.fromDate,
            toDate: this.toDate,
            referenceNumber: this.referenceNumber
        };
        return WireSearch.init(vals);
    }
    
    static init(record) {
        const vals = { 
            status: "any status",
            fundingAccountID: "all accounts",
            dateRange: "custom",
            fromDate: BCMSDate.init().offset(-7),
            toDate: undefined,
            referenceNumber: undefined,
            ...record ?? {} 
        }
        return new WireSearch(vals);
    }
}